<template>
  <div class="gl-form-page">

    <!-- Page header -->
    <div class="container-fluid">
      <div class="gl-form-header">
        <div>
          <div class="gl-form-breadcrumb">
            <router-link to="/">Home</router-link>
            <span>/</span>
            <router-link to="/owner/products">Products</router-link>
            <span>/</span>
            <span>{{ isEdit ? 'Edit' : 'Create' }}</span>
          </div>
          <h1 class="gl-form-title">{{ isEdit ? 'Edit Product' : 'Create Product' }}</h1>
        </div>
      </div>
    </div>

    <div class="container-fluid">
      <!-- Loading -->
      <div v-if="loading" class="gl-loading">
        <div class="spinner-border" role="status" style="color:#900C27;width:28px;height:28px;border-width:3px"></div>
        <span>Loading product…</span>
      </div>

      <div v-else class="gl-prod-layout">

        <!-- LEFT: Form -->
        <form @submit.prevent="submitForm" class="gl-form-body">

          <!-- Live preview strip -->
          <div class="gl-preview-strip" v-if="form.name">
            <div class="gl-preview-img">
              <img v-if="form.image" :src="form.image" class="gl-preview-img-real">
              <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/></svg>
            </div>
            <div class="gl-preview-info">
              <div class="gl-preview-name">{{ form.name }}</div>
              <div class="gl-preview-meta">
                <span v-if="form.category_id" class="gl-preview-tag">{{ getCategoryName(form.category_id) }}</span>
                <span v-if="form.base_price" class="gl-preview-price">RM {{ formatPrice(form.base_price) }}</span>
              </div>
            </div>
            <div :class="['gl-preview-status', form.is_active ? 'active' : 'inactive']">
              <span class="gl-preview-dot"></span>{{ form.is_active ? 'Active' : 'Inactive' }}
            </div>
          </div>

          <!-- ── Section 1: Basic Information ── -->
          <div class="gl-section" style="animation-delay:0ms">
            <div class="gl-section-header">
              <div class="gl-section-icon">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>
              </div>
              <div>
                <div class="gl-section-title">Basic Information</div>
                <div class="gl-section-desc">Product name, category, pricing and description</div>
              </div>
            </div>

            <div class="gl-fields">
              <div class="gl-field-row">
                <div class="gl-field" style="flex:2">
                  <label class="gl-label">Product Name <span class="gl-req">*</span></label>
                  <input type="text" class="gl-input" :class="{ 'gl-input-err': errors.name }" v-model="form.name" placeholder="e.g. Nasi Lemak Special" required>
                  <div v-if="errors.name" class="gl-err">{{ errors.name[0] }}</div>
                </div>
                <div class="gl-field" style="flex:1">
                  <label class="gl-label">SKU <span class="gl-optional">(optional)</span></label>
                  <input type="text" class="gl-input" :class="{ 'gl-input-err': errors.sku }" v-model="form.sku" placeholder="Auto-generated">
                  <div class="gl-hint">Leave blank to auto-generate</div>
                  <div v-if="errors.sku" class="gl-err">{{ errors.sku[0] }}</div>
                </div>
              </div>

              <div class="gl-field-row">
                <div class="gl-field">
                  <label class="gl-label">Category <span class="gl-req">*</span></label>
                  <select class="gl-input gl-select" :class="{ 'gl-input-err': errors.category_id }" v-model="form.category_id" required>
                    <option value="">Select Category</option>
                    <option v-for="cat in categories" :key="cat.id" :value="cat.id">{{ cat.name }}</option>
                  </select>
                  <div v-if="errors.category_id" class="gl-err">{{ errors.category_id[0] }}</div>
                </div>
                <div class="gl-field">
                  <label class="gl-label">Base Price (RM) <span class="gl-req">*</span></label>
                  <div class="gl-input-prefix-wrap">
                    <span class="gl-input-prefix">RM</span>
                    <input type="number" step="0.01" min="0" class="gl-input gl-input-prefixed" :class="{ 'gl-input-err': errors.base_price }" v-model="form.base_price" placeholder="0.00" required>
                  </div>
                  <div class="gl-hint">Branches can override per-branch</div>
                  <div v-if="errors.base_price" class="gl-err">{{ errors.base_price[0] }}</div>
                </div>
              </div>

              <div class="gl-field">
                <label class="gl-label">Description <span class="gl-optional">(optional)</span></label>
                <textarea class="gl-input gl-textarea" :class="{ 'gl-input-err': errors.description }" v-model="form.description" rows="3" placeholder="e.g. Fragrant coconut rice served with sambal, fried anchovies, peanuts, cucumber, and a boiled egg"></textarea>
                <div v-if="errors.description" class="gl-err">{{ errors.description[0] }}</div>
              </div>

              <!-- Image upload -->
              <div class="gl-field">
                <label class="gl-label">Product Image <span class="gl-optional">(optional)</span></label>
                <div :class="['gl-upload-zone', { 'gl-upload-has-img': form.image }]" @click="$refs.productImageInput.click()">
                  <input ref="productImageInput" type="file" accept="image/jpeg,image/png,image/jpg,image/webp" class="gl-file-input" @change="handleImageUpload">
                  <div v-if="uploadingImage" class="gl-upload-state">
                    <div class="spinner-border" style="color:#900C27;width:18px;height:18px;border-width:2px"></div>
                    <span>Uploading…</span>
                  </div>
                  <div v-else-if="form.image" class="gl-upload-preview">
                    <img :src="form.image" class="gl-upload-preview-img">
                    <div class="gl-upload-replace">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                      Click to replace
                    </div>
                  </div>
                  <div v-else class="gl-upload-state">
                    <div class="gl-upload-icon">
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                    </div>
                    <span class="gl-upload-label">Click to upload image</span>
                    <span class="gl-upload-hint">JPEG, PNG or WebP · max 4MB</span>
                  </div>
                </div>
                <div v-if="imageError" class="gl-err" style="margin-top:6px">{{ imageError }}</div>
                <div v-if="errors.image" class="gl-err" style="margin-top:6px">{{ errors.image[0] }}</div>
              </div>
            </div>
          </div>

          <!-- ── Section 2: Status ── -->
          <div class="gl-section" style="animation-delay:60ms">
            <div class="gl-section-header">
              <div class="gl-section-icon">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
              </div>
              <div>
                <div class="gl-section-title">Status</div>
                <div class="gl-section-desc">Control product visibility and featured placement</div>
              </div>
            </div>
            <div class="gl-status-row">
              <button type="button" :class="['gl-status-item', { on: form.is_active }]" @click="form.is_active = !form.is_active">
                <span class="gl-status-text">
                  <span class="gl-status-label">Active</span>
                  <span class="gl-status-sub">Product is visible and can be ordered</span>
                </span>
                <span :class="['gl-toggle', { on: form.is_active }]">
                  <span class="gl-toggle-knob"></span>
                </span>
              </button>
              <button type="button" :class="['gl-status-item', { on: form.is_featured }]" @click="form.is_featured = !form.is_featured">
                <span class="gl-status-text">
                  <span class="gl-status-label">Featured</span>
                  <span class="gl-status-sub">Highlighted on the customer home screen</span>
                </span>
                <span :class="['gl-toggle', { on: form.is_featured }]">
                  <span class="gl-toggle-knob"></span>
                </span>
              </button>
            </div>
          </div>

          <!-- ── Section 3: Assign to Branches (create only) ── -->
          <div v-if="!isEdit" class="gl-section" style="animation-delay:120ms">
            <div class="gl-section-header">
              <div class="gl-section-icon">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
              </div>
              <div>
                <div class="gl-section-title">Assign to Branches</div>
                <div class="gl-section-desc">Select which branches will have this product available</div>
              </div>
            </div>
            <div class="gl-branch-cards">
              <button
                v-for="branch in branches"
                :key="branch.id"
                type="button"
                :class="['gl-branch-card', { active: form.outlet_ids.includes(branch.id) }]"
                @click="toggleBranch(branch.id)"
              >
                <div class="gl-bcard-avatar">{{ branch.name.charAt(0).toUpperCase() }}{{ (branch.name.split(' ')[1] || '').charAt(0).toUpperCase() }}</div>
                <div class="gl-bcard-info">
                  <div class="gl-bcard-name">{{ branch.name }}</div>
                  <div class="gl-bcard-loc">{{ branch.city }}, {{ branch.state }}</div>
                </div>
                <div class="gl-bcard-check">
                  <svg v-if="form.outlet_ids.includes(branch.id)" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"><polyline points="20 6 9 17 4 12"/></svg>
                </div>
              </button>
            </div>
          </div>

          <!-- ── Section 4: Add-ons ── -->
          <div class="gl-section" style="animation-delay:180ms">
            <div class="gl-section-header">
              <div class="gl-section-icon">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="4" y1="21" x2="4" y2="14"/><line x1="4" y1="10" x2="4" y2="3"/><line x1="12" y1="21" x2="12" y2="12"/><line x1="12" y1="8" x2="12" y2="3"/><line x1="20" y1="21" x2="20" y2="16"/><line x1="20" y1="12" x2="20" y2="3"/><line x1="1" y1="14" x2="7" y2="14"/><line x1="9" y1="8" x2="15" y2="8"/><line x1="17" y1="16" x2="23" y2="16"/></svg>
              </div>
              <div class="gl-section-titlewrap">
                <div class="gl-section-titlerow">
                  <span class="gl-section-title">Add-ons</span>
                  <span v-if="selectedAddonCount" class="gl-addon-count-pill">{{ selectedAddonCount }} selected</span>
                </div>
                <div class="gl-section-desc">Toggle on the reusable add-ons customers can pick for this item</div>
              </div>
              <router-link to="/owner/addons" class="gl-manage-addons-link">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><line x1="4" y1="21" x2="4" y2="14"/><line x1="4" y1="10" x2="4" y2="3"/><line x1="12" y1="21" x2="12" y2="12"/><line x1="12" y1="8" x2="12" y2="3"/><line x1="20" y1="21" x2="20" y2="16"/><line x1="20" y1="12" x2="20" y2="3"/><line x1="1" y1="14" x2="7" y2="14"/><line x1="9" y1="8" x2="15" y2="8"/><line x1="17" y1="16" x2="23" y2="16"/></svg>
                Manage
              </router-link>
            </div>

            <!-- Empty: no addons defined yet -->
            <div v-if="availableAddons.length === 0" class="gl-addons-empty">
              <p>You haven't created any add-ons yet.</p>
              <router-link to="/owner/addons/create" class="gl-addons-empty-btn">Create an add-on</router-link>
            </div>

            <!-- Addon toggles, grouped by group label -->
            <div v-else class="gl-addon-groups">
              <div v-for="grp in groupedAddons" :key="grp.label" class="gl-addon-group">
                <div class="gl-addon-group-head">
                  <div class="gl-addon-group-label">
                    {{ grp.label }}
                    <span class="gl-addon-group-count">{{ groupSelectedCount(grp) }}/{{ grp.items.length }}</span>
                  </div>
                  <button type="button" class="gl-addon-group-action" @click="toggleGroupAll(grp)">
                    {{ allSelectedInGroup(grp) ? 'Clear' : 'Select all' }}
                  </button>
                </div>
                <div class="gl-addon-rows">
                  <div
                    v-for="addon in grp.items"
                    :key="addon.id"
                    :class="['gl-addon-row', { selected: isAddonSelected(addon.id), unavailable: !addon.is_available }]"
                  >
                    <button type="button" class="gl-addon-hit" @click="toggleAddon(addon.id)">
                      <span class="gl-addon-info">
                        <span class="gl-addon-name">{{ addon.name }}</span>
                        <span v-if="addon.sku" class="gl-addon-sku">{{ addon.sku }}</span>
                        <span v-if="!addon.is_available" class="gl-addon-oos">Out of stock</span>
                      </span>
                      <span class="gl-addon-right">
                        <span v-if="addon.price > 0" class="gl-addon-price">+RM{{ formatPrice(addon.price) }}</span>
                        <span v-else class="gl-addon-free">Free</span>
                        <span :class="['gl-toggle', { on: isAddonSelected(addon.id) }]">
                          <span class="gl-toggle-knob"></span>
                        </span>
                      </span>
                    </button>

                    <!-- Per-menu stock: only relevant once the add-on is on this product -->
                    <div v-if="isAddonSelected(addon.id)" class="gl-addon-perprod">
                      <span class="gl-addon-perprod-label">
                        {{ addonOverrideLabel(addon) }}
                      </span>
                      <button
                        type="button"
                        :class="['gl-toggle', 'gl-toggle-sm', { on: addonOnForProduct(addon.id) }]"
                        :disabled="!addon.is_available"
                        :title="addon.is_available
                          ? 'Turn this add-on on or off for this menu only'
                          : 'This add-on is out of stock everywhere — turn it back on from the Add-ons page'"
                        @click="toggleAddonForProduct(addon.id)"
                      >
                        <span class="gl-toggle-knob"></span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- ── Section 5: Product Details ── -->
          <div class="gl-section" style="animation-delay:240ms">
            <div class="gl-section-header">
              <div class="gl-section-icon">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
              </div>
              <div>
                <div class="gl-section-title">Product Details</div>
                <div class="gl-section-desc">Nutritional info, allergens, and dietary tags</div>
              </div>
            </div>

            <div class="gl-fields">
              <div class="gl-field-row">
                <div class="gl-field">
                  <label class="gl-label">Calories (kcal) <span class="gl-optional">(optional)</span></label>
                  <input type="number" min="0" class="gl-input" v-model="form.attributes.calories" placeholder="e.g. 450">
                </div>
                <div class="gl-field">
                  <label class="gl-label">Prep Time (min) <span class="gl-optional">(optional)</span></label>
                  <input type="number" min="0" class="gl-input" v-model="form.attributes.prep_time" placeholder="e.g. 15">
                </div>
              </div>

              <div class="gl-field">
                <label class="gl-label">Allergens</label>
                <div class="gl-pill-group">
                  <button
                    v-for="allergen in allergenOptions"
                    :key="allergen"
                    type="button"
                    :class="['gl-pill', { active: form.attributes.allergens.includes(allergen) }]"
                    @click="toggleTag('allergens', allergen)"
                  >{{ allergen }}</button>
                </div>
              </div>

              <div class="gl-field">
                <label class="gl-label">Dietary</label>
                <div class="gl-pill-group">
                  <button
                    v-for="tag in dietaryOptions"
                    :key="tag"
                    type="button"
                    :class="['gl-pill gl-pill-green', { active: form.attributes.dietary.includes(tag) }]"
                    @click="toggleTag('dietary', tag)"
                  >{{ tag }}</button>
                </div>
              </div>
            </div>
          </div>

          <!-- ── Actions (desktop) ── -->
          <div class="gl-form-actions" style="animation-delay:280ms">
            <router-link to="/owner/products" class="gl-btn-cancel">Cancel</router-link>
            <button type="submit" class="gl-btn-submit" :disabled="submitting">
              <span v-if="submitting" class="spinner-border spinner-border-sm" style="width:14px;height:14px;border-width:2px"></span>
              <svg v-else width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><polyline points="20 6 9 17 4 12"/></svg>
              {{ isEdit ? 'Save Changes' : 'Create Product' }}
            </button>
          </div>

          <!-- ── Sticky bar (mobile only) ── -->
          <div class="gl-sticky-bar">
            <router-link to="/owner/products" class="gl-btn-cancel gl-sticky-cancel">Cancel</router-link>
            <button type="submit" class="gl-btn-submit gl-sticky-submit" :disabled="submitting">
              <span v-if="submitting" class="spinner-border spinner-border-sm" style="width:14px;height:14px;border-width:2px"></span>
              <svg v-else width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><polyline points="20 6 9 17 4 12"/></svg>
              {{ isEdit ? 'Save Changes' : 'Create Product' }}
            </button>
          </div>
        </form>

        <!-- RIGHT: Preview sidebar -->
        <div class="gl-prod-sidebar">
          <div class="gl-sidebar-card gl-preview-card" style="animation-delay:60ms">
            <div class="gl-sidebar-card-header">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>
              Customer Preview
            </div>

            <!-- Product image -->
            <div class="gl-cp-img-wrap">
              <img v-if="form.image && !previewImgError" :src="form.image" class="gl-cp-img" @error="previewImgError = true">
              <div v-else class="gl-cp-img-placeholder">
                <span style="font-size:48px">🍽️</span>
              </div>
            </div>

            <!-- Name / desc / price -->
            <div class="gl-cp-body">
              <div class="gl-cp-name">{{ form.name || 'Product Name' }}</div>
              <div class="gl-cp-desc">{{ form.description || 'No description' }}</div>
              <div class="gl-cp-price">RM {{ formatPrice(form.base_price) }}</div>

              <!-- Metadata badges -->
              <div v-if="form.attributes.calories || form.attributes.prep_time || form.attributes.dietary.length || form.attributes.allergens.length" class="gl-cp-badges">
                <span v-if="form.attributes.calories" class="gl-cp-badge gl-badge-cal">🔥 {{ form.attributes.calories }} kcal</span>
                <span v-if="form.attributes.prep_time" class="gl-cp-badge gl-badge-time">⏱ {{ form.attributes.prep_time }} min</span>
                <span v-for="tag in form.attributes.dietary" :key="tag" class="gl-cp-badge gl-badge-green">{{ tag }}</span>
                <span v-for="a in form.attributes.allergens" :key="a" class="gl-cp-badge gl-badge-red">⚠ {{ a }}</span>
              </div>

              <!-- Add-ons preview -->
              <div v-if="selectedAddons.length" class="gl-cp-options">
                <div v-for="grp in selectedGroupedAddons" :key="grp.label" class="gl-cp-og">
                  <div class="gl-cp-og-name">{{ grp.label }}</div>
                  <div class="gl-cp-og-pills">
                    <span v-for="addon in grp.items" :key="addon.id" class="gl-cp-opt-pill">
                      {{ addon.name }}<span v-if="addon.price > 0" class="gl-cp-opt-price">+RM{{ formatPrice(addon.price) }}</span>
                    </span>
                  </div>
                </div>
              </div>

              <!-- Special instructions -->
              <div class="gl-cp-instructions">
                <div class="gl-cp-inst-label">Special Instructions</div>
                <div class="gl-cp-inst-input">E.g. less sugar, no ice…</div>
              </div>

              <!-- Cart buttons -->
              <div class="gl-cp-cta">
                <button type="button" class="gl-cp-btn-secondary" disabled>Buy Now</button>
                <button type="button" class="gl-cp-btn-primary" disabled>Add To Cart</button>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>

    <!-- Upgrade Modal -->
    <UpgradeModal
      ref="upgradeModal"
      modal-id="productUpgradeModal"
      title="Product Limit Reached"
      :message="upgradeMessage"
      :current-plan="currentPlan"
      resource-label="Products"
      :used="usageUsed"
      :limit="usageLimit"
    />
  </div>
</template>

<script>
import { ownerProductApi, ownerCategoryApi, ownerBranchApi, ownerAddonApi } from '@/services/ownerApi';
import { subscriptionApi } from '@/services/subscriptionApi';
import UpgradeModal from '@/components/owner/common/UpgradeModal.vue';

export default {
  name: 'ProductForm',
  components: { UpgradeModal },

  data() {
    return {
      loading: false,
      submitting: false,
      uploadingImage: false,
      imageError: '',
      previewImgError: false,
      isEdit: false,
      errors: {},
      categories: [],
      branches: [],
      availableAddons: [],
      upgradeMessage: '',
      currentPlan: '',
      usageUsed: 0,
      usageLimit: 0,
      form: {
        name: '',
        sku: '',
        description: '',
        image: '',
        category_id: '',
        base_price: '',
        is_active: true,
        is_featured: false,
        outlet_ids: [],
        addon_ids: [],
        // addon_id -> true/false/null override for THIS product only.
        // null (or missing) means the add-on follows its central stock toggle.
        addon_availability: {},
        attributes: {
          calories: '',
          prep_time: '',
          allergens: [],
          dietary: []
        }
      },
      allergenOptions: ['nuts', 'dairy', 'gluten', 'shellfish', 'eggs', 'soy', 'wheat'],
      dietaryOptions: ['halal', 'vegetarian', 'vegan', 'gluten-free']
    };
  },

  watch: {
    'form.image'() { this.previewImgError = false; }
  },

  computed: {
    // All addons grouped by their label, for the picker
    groupedAddons() {
      return this.groupBy(this.availableAddons);
    },
    // The addon objects currently attached to this product
    selectedAddons() {
      return this.availableAddons.filter(a => this.form.addon_ids.includes(a.id));
    },
    // Selected addons grouped by label, for the live preview
    selectedGroupedAddons() {
      return this.groupBy(this.selectedAddons);
    },
    // Total number of addons attached to this product
    selectedAddonCount() {
      return this.form.addon_ids.length;
    }
  },

  mounted() {
    this.fetchCategories();
    this.fetchBranches();
    this.fetchAddons();
    if (this.$route.params.id) {
      this.isEdit = true;
      this.fetchProduct();
    }
  },

  methods: {
    formatPrice(price) {
      return price ? parseFloat(price).toFixed(2) : '0.00';
    },

    getCategoryName(id) {
      return this.categories.find(c => c.id === id)?.name || '';
    },

    toggleBranch(id) {
      const idx = this.form.outlet_ids.indexOf(id);
      if (idx === -1) this.form.outlet_ids.push(id);
      else this.form.outlet_ids.splice(idx, 1);
    },

    toggleTag(type, value) {
      const arr = this.form.attributes[type];
      const idx = arr.indexOf(value);
      if (idx === -1) arr.push(value);
      else arr.splice(idx, 1);
    },

    async handleImageUpload(e) {
      const file = e.target.files?.[0];
      if (!file) return;
      if (file.size > 4 * 1024 * 1024) { this.imageError = 'Image must be under 4MB.'; return; }
      this.imageError = '';
      this.uploadingImage = true;
      try {
        const formData = new FormData();
        formData.append('image', file);
        const response = await ownerProductApi.uploadImage(formData);
        this.form.image = response.data.url;
      } catch (err) {
        this.imageError = err?.response?.data?.message ?? 'Upload failed. Please try again.';
      } finally {
        this.uploadingImage = false;
        e.target.value = '';
      }
    },

    async fetchCategories() {
      try {
        const response = await ownerCategoryApi.list({ per_page: 100 });
        this.categories = response.data.data;
      } catch (error) {
        console.error('Failed to fetch categories:', error);
      }
    },

    async fetchBranches() {
      try {
        const response = await ownerBranchApi.list({ per_page: 100 });
        this.branches = response.data.data;
        if (!this.isEdit) this.form.outlet_ids = this.branches.map(b => b.id);
      } catch (error) {
        console.error('Failed to fetch branches:', error);
      }
    },

    async fetchProduct() {
      try {
        this.loading = true;
        const response = await ownerProductApi.get(this.$route.params.id);
        const product = response.data.data;
        this.form = {
          name: product.name,
          sku: product.sku || '',
          description: product.description || '',
          image: product.image || '',
          category_id: product.category_id,
          base_price: product.base_price,
          is_active: product.is_active,
          is_featured: product.is_featured || false,
          outlet_ids: product.outlets?.map(o => o.id) || [],
          addon_ids: (product.addons || []).map(a => a.id),
          addon_availability: (product.addons || []).reduce((acc, a) => {
            const override = a.pivot?.is_available;
            acc[a.id] = override === null || override === undefined ? null : !!override;
            return acc;
          }, {}),
          attributes: {
            calories: product.attributes?.calories || '',
            prep_time: product.attributes?.prep_time || '',
            allergens: product.attributes?.allergens || [],
            dietary: product.attributes?.dietary || []
          }
        };
      } catch (error) {
        console.error('Failed to fetch product:', error);
        this.$router.push('/owner/products');
      } finally {
        this.loading = false;
      }
    },

    buildPayload() {
      const payload = { ...this.form };
      payload.attributes = {
        calories: this.form.attributes.calories !== '' ? parseInt(this.form.attributes.calories) : null,
        prep_time: this.form.attributes.prep_time !== '' ? parseInt(this.form.attributes.prep_time) : null,
        allergens: this.form.attributes.allergens,
        dietary: this.form.attributes.dietary
      };
      payload.addon_ids = [...this.form.addon_ids];
      // Only send overrides for add-ons still attached, so a detached one can't
      // resurrect a stale value on the server.
      payload.addon_availability = this.form.addon_ids.reduce((acc, id) => {
        acc[id] = this.form.addon_availability[id] ?? null;
        return acc;
      }, {});
      return payload;
    },

    async submitForm() {
      try {
        this.submitting = true;
        this.errors = {};
        const payload = this.buildPayload();
        if (this.isEdit) {
          await ownerProductApi.update(this.$route.params.id, payload);
        } else {
          await ownerProductApi.create(payload);
        }
        this.$router.push('/owner/products');
      } catch (error) {
        console.error('Failed to save product:', error);
        if (error.response?.data?.errors) {
          this.errors = error.response.data.errors;
        } else {
          const message = error.response?.data?.message || 'Failed to save product';
          if (this.isPlanLimitError(message)) this.showUpgradeModal(message);
          else alert(message);
        }
      } finally {
        this.submitting = false;
      }
    },

    async fetchAddons() {
      try {
        const response = await ownerAddonApi.list();
        this.availableAddons = response.data.data;
      } catch (error) {
        console.error('Failed to fetch addons:', error);
      }
    },

    isAddonSelected(id) {
      return this.form.addon_ids.includes(id);
    },

    // Whether the add-on is switched on for this product specifically.
    // Missing/null means it inherits the central toggle, which reads as "on".
    addonOnForProduct(id) {
      return this.form.addon_availability[id] !== false;
    },

    addonOverrideLabel(addon) {
      if (!addon.is_available) return 'Out of stock everywhere';
      if (this.form.addon_availability[addon.id] === false) return 'Off for this menu';
      return 'On for this menu';
    },

    toggleAddonForProduct(id) {
      const addon = this.availableAddons.find(a => a.id === id);
      if (!addon || !addon.is_available) return;
      // Clear back to null (inherit) rather than storing an explicit true, so the
      // add-on keeps following its central toggle unless deliberately switched off.
      this.form.addon_availability[id] = this.addonOnForProduct(id) ? false : null;
    },

    toggleAddon(id) {
      const idx = this.form.addon_ids.indexOf(id);
      if (idx === -1) {
        this.form.addon_ids.push(id);
      } else {
        this.form.addon_ids.splice(idx, 1);
        // Detaching drops any per-menu override with it.
        delete this.form.addon_availability[id];
      }
    },

    groupSelectedCount(grp) {
      return grp.items.filter(a => this.form.addon_ids.includes(a.id)).length;
    },

    allSelectedInGroup(grp) {
      return grp.items.length > 0 && grp.items.every(a => this.form.addon_ids.includes(a.id));
    },

    toggleGroupAll(grp) {
      if (this.allSelectedInGroup(grp)) {
        // Clear: remove every addon in this group
        const ids = grp.items.map(a => a.id);
        this.form.addon_ids = this.form.addon_ids.filter(id => !ids.includes(id));
      } else {
        // Select all: add any not yet selected
        grp.items.forEach(a => {
          if (!this.form.addon_ids.includes(a.id)) this.form.addon_ids.push(a.id);
        });
      }
    },

    // Group a list of addons by group_label (ungrouped ones fall under "Add-ons")
    groupBy(list) {
      const groups = {};
      for (const addon of list) {
        const label = addon.group_label || 'Add-ons';
        if (!groups[label]) groups[label] = [];
        groups[label].push(addon);
      }
      return Object.keys(groups).map(label => ({ label, items: groups[label] }));
    },

    isPlanLimitError(message) {
      return ['limit', 'maximum', 'exceeded', 'upgrade', 'plan'].some(k => message.toLowerCase().includes(k));
    },

    async showUpgradeModal(message) {
      this.upgradeMessage = message;
      try {
        const response = await subscriptionApi.getUsage();
        const data = response.data.data;
        this.currentPlan = data.plan?.name || 'Free';
        this.usageUsed = data.limits?.products?.used || 0;
        this.usageLimit = data.limits?.products?.limit || 0;
      } catch (e) {
        console.error('Failed to fetch usage:', e);
      }
      this.$refs.upgradeModal?.show();
    }
  }
};
</script>

<style scoped>
/* ── Page ── */
.gl-form-page { padding-bottom: 48px; }

/* ── Header ── */
.gl-form-header {
  display: flex; align-items: flex-end;
  padding: 28px 0 20px;
}
.gl-form-breadcrumb {
  display: flex; align-items: center; gap: 6px;
  font-size: 12.5px; color: #9ca3af; margin-bottom: 4px;
}
.gl-form-breadcrumb a { color: #9ca3af; text-decoration: none; }
.gl-form-breadcrumb a:hover { color: #900C27; }
.gl-form-title {
  font-size: 22px; font-weight: 700; color: #111318;
  letter-spacing: -0.4px; margin: 0;
}
.gl-loading {
  display: flex; align-items: center; gap: 12px;
  padding: 60px 0; justify-content: center; font-size: 14px; color: #9ca3af;
}

/* ── Two-col layout ── */
.gl-prod-layout {
  display: grid;
  grid-template-columns: 1fr 300px;
  gap: 16px;
  align-items: start;
}
.gl-form-body { display: flex; flex-direction: column; gap: 12px; }

/* ── Preview strip ── */
.gl-preview-strip {
  display: flex; align-items: center; gap: 12px;
  background: #fff; border: 1.5px solid rgba(144,12,39,0.2);
  border-radius: 14px; padding: 12px 16px;
  animation: gl-fade-in 0.25s ease;
}
@keyframes gl-fade-in {
  from { opacity: 0; transform: translateY(-4px); }
  to   { opacity: 1; transform: translateY(0); }
}
.gl-preview-img {
  width: 40px; height: 40px; border-radius: 10px; flex-shrink: 0; overflow: hidden;
  background: linear-gradient(135deg, #f0faf8, #e0f2ee);
  color: rgba(144,12,39,0.4);
  display: flex; align-items: center; justify-content: center;
}
.gl-preview-img-real { width: 100%; height: 100%; object-fit: cover; }
.gl-preview-info { flex: 1; min-width: 0; }
.gl-preview-name { font-size: 14px; font-weight: 700; color: #111318; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.gl-preview-meta { display: flex; gap: 6px; flex-wrap: wrap; margin-top: 3px; }
.gl-preview-tag {
  font-size: 11px; background: #f3f4f6; color: #6b7280;
  padding: 2px 8px; border-radius: 20px; font-weight: 500;
}
.gl-preview-price {
  font-size: 11px; background: rgba(144,12,39,0.08); color: #900C27;
  padding: 2px 8px; border-radius: 20px; font-weight: 700;
}
.gl-preview-status {
  display: flex; align-items: center; gap: 5px;
  font-size: 11.5px; font-weight: 600; padding: 4px 10px;
  border-radius: 20px; flex-shrink: 0;
}
.gl-preview-status.active { background: rgba(144,12,39,0.10); color: #900C27; }
.gl-preview-status.inactive { background: #f3f4f6; color: #9ca3af; }
.gl-preview-dot {
  width: 6px; height: 6px; border-radius: 50%; background: currentColor; flex-shrink: 0;
}
.gl-preview-status.active .gl-preview-dot { animation: gl-pulse 2s ease infinite; }
@keyframes gl-pulse {
  0%   { box-shadow: 0 0 0 0 rgba(144,12,39,0.4); }
  70%  { box-shadow: 0 0 0 5px rgba(144,12,39,0); }
  100% { box-shadow: 0 0 0 0 rgba(144,12,39,0); }
}

/* ── Sections ── */
.gl-section {
  background: #fff; border: 1.5px solid #f0f1f3;
  border-radius: 16px; padding: 22px;
  animation: gl-sect-in 0.3s ease both;
}
@keyframes gl-sect-in {
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
}
.gl-section-header {
  display: flex; align-items: center; gap: 12px; margin-bottom: 20px;
}
.gl-section-icon {
  width: 34px; height: 34px; border-radius: 9px;
  background: rgba(144,12,39,0.10); color: #900C27;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.gl-section-title { font-size: 14px; font-weight: 700; color: #111318; }
.gl-section-desc { font-size: 12px; color: #9ca3af; margin-top: 1px; }

/* ── Fields ── */
.gl-fields { display: flex; flex-direction: column; gap: 16px; }
.gl-field-row { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
.gl-field { display: flex; flex-direction: column; gap: 5px; }
.gl-label { font-size: 12.5px; font-weight: 600; color: #374151; }
.gl-req { color: #ef4444; margin-left: 1px; }
.gl-optional { color: #9ca3af; font-weight: 400; margin-left: 2px; }
.gl-hint { font-size: 11px; color: #9ca3af; }
.gl-err { font-size: 11.5px; color: #ef4444; }

.gl-input {
  height: 40px; border: 1.5px solid #e5e7eb; border-radius: 10px;
  padding: 0 12px; font-size: 13.5px; color: #111318;
  background: #fafafa; outline: none; width: 100%;
  transition: border-color 0.15s, box-shadow 0.15s, background 0.15s;
}
.gl-input:focus { border-color: #900C27; background: #fff; box-shadow: 0 0 0 3px rgba(144,12,39,0.10); }
.gl-input::placeholder { color: #c4c8d0; }
.gl-input-err { border-color: #ef4444 !important; }
.gl-select {
  appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%239ca3af' stroke-width='2' stroke-linecap='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
  background-repeat: no-repeat; background-position: right 12px center;
  padding-right: 36px; cursor: pointer;
}
.gl-textarea {
  height: auto; padding: 10px 12px; resize: vertical; min-height: 80px; line-height: 1.5;
}
.gl-input-prefix-wrap { position: relative; }
.gl-input-prefix {
  position: absolute; left: 12px; top: 50%; transform: translateY(-50%);
  font-size: 12px; font-weight: 600; color: #9ca3af; pointer-events: none;
}
.gl-input-prefixed { padding-left: 38px; }

/* ── Image upload ── */
.gl-file-input { display: none; }
.gl-upload-zone {
  border: 2px dashed #e5e7eb; border-radius: 12px;
  cursor: pointer; transition: all 0.2s; overflow: hidden;
  background: #fafafa;
}
.gl-upload-zone:hover { border-color: #900C27; background: rgba(144,12,39,0.03); }
.gl-upload-has-img { border-style: solid; border-color: rgba(144,12,39,0.2); }
.gl-upload-state {
  display: flex; flex-direction: column; align-items: center;
  gap: 8px; padding: 28px 16px; color: #9ca3af;
}
.gl-upload-icon {
  width: 44px; height: 44px; border-radius: 12px;
  background: #f3f4f6; color: #9ca3af;
  display: flex; align-items: center; justify-content: center;
}
.gl-upload-label { font-size: 13px; font-weight: 600; color: #374151; }
.gl-upload-hint { font-size: 11.5px; }
.gl-upload-preview { position: relative; }
.gl-upload-preview-img { width: 100%; max-height: 180px; object-fit: cover; display: block; }
.gl-upload-replace {
  position: absolute; inset: 0;
  background: rgba(0,0,0,0.45); color: #fff;
  display: flex; align-items: center; justify-content: center;
  gap: 7px; font-size: 13px; font-weight: 600; opacity: 0;
  transition: opacity 0.2s;
}
.gl-upload-zone:hover .gl-upload-replace { opacity: 1; }

/* ── Status section ── */
.gl-status-row { display: flex; flex-direction: column; gap: 10px; }
.gl-status-item {
  display: flex; align-items: center; justify-content: space-between; gap: 16px;
  width: 100%; text-align: left; cursor: pointer;
  padding: 14px 16px; border-radius: 12px;
  border: 1.5px solid #eceef1; background: #fbfbfc;
  transition: border-color 0.15s, background 0.15s;
}
.gl-status-item:hover { border-color: rgba(144,12,39,0.3); background: #fff; }
.gl-status-item.on { border-color: #900C27; background: rgba(144,12,39,0.06); }
.gl-status-text { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.gl-status-label { font-size: 13.5px; font-weight: 600; color: #111318; }
.gl-status-sub { font-size: 12px; color: #9ca3af; }
.gl-status-item .gl-toggle { display: inline-block; flex-shrink: 0; }

/* ── Toggle ── */
.gl-toggle {
  width: 44px; height: 24px; border-radius: 12px;
  border: none; cursor: pointer; padding: 0;
  background: #e5e7eb; position: relative;
  transition: background 0.2s; flex-shrink: 0;
}
.gl-toggle.on { background: #900C27; }
.gl-toggle-knob {
  position: absolute; top: 3px; left: 3px;
  width: 18px; height: 18px; border-radius: 50%;
  background: #fff; transition: transform 0.2s;
  box-shadow: 0 1px 4px rgba(0,0,0,0.2); display: block;
}
.gl-toggle.on .gl-toggle-knob { transform: translateX(20px); }

/* ── Add-on picker ── */
.gl-section-titlewrap { flex: 1; min-width: 0; }
.gl-section-titlerow { display: flex; align-items: center; gap: 8px; }
.gl-addon-count-pill {
  font-size: 11px; font-weight: 700; color: #900C27; background: rgba(144,12,39,0.10);
  padding: 2px 9px; border-radius: 20px; white-space: nowrap;
}
.gl-manage-addons-link {
  display: inline-flex; align-items: center; gap: 5px; flex-shrink: 0; white-space: nowrap;
  font-size: 12.5px; font-weight: 600; color: #900C27; text-decoration: none;
  background: rgba(144,12,39,0.08); padding: 7px 13px; border-radius: 8px; transition: background 0.15s;
}
.gl-manage-addons-link:hover { background: rgba(144,12,39,0.16); color: #900C27; }
.gl-addons-empty {
  display: flex; flex-direction: column; align-items: center; gap: 10px;
  padding: 28px 16px; text-align: center; border: 1.5px dashed #e5e7eb; border-radius: 12px;
}
.gl-addons-empty p { font-size: 13px; color: #9ca3af; margin: 0; }
.gl-addons-empty-btn {
  font-size: 12.5px; font-weight: 600; color: #fff; background: #900C27;
  padding: 8px 16px; border-radius: 8px; text-decoration: none; transition: background 0.15s;
}
.gl-addons-empty-btn:hover { background: #6f0a1e; color: #fff; }
.gl-addon-groups { display: flex; flex-direction: column; gap: 18px; }
.gl-addon-group-head {
  display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 8px;
}
.gl-addon-group-label {
  display: flex; align-items: center; gap: 7px;
  font-size: 11px; font-weight: 700; color: #9ca3af; text-transform: uppercase; letter-spacing: 0.6px;
}
.gl-addon-group-count {
  font-size: 10.5px; font-weight: 700; color: #6b7280; background: #f3f4f6;
  padding: 1px 7px; border-radius: 20px; letter-spacing: 0;
}
.gl-addon-group-action {
  border: none; background: transparent; cursor: pointer; padding: 2px 4px;
  font-size: 11.5px; font-weight: 600; color: #900C27; transition: opacity 0.15s;
}
.gl-addon-group-action:hover { opacity: 0.7; }
.gl-addon-rows { display: flex; flex-direction: column; gap: 8px; }
.gl-addon-row {
  position: relative; border-radius: 11px;
  border: 1.5px solid #eceef1; background: #fbfbfc; overflow: hidden;
  transition: border-color 0.15s, background 0.15s;
}
/* The clickable attach/detach area keeps the row's original layout */
.gl-addon-hit {
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
  width: 100%; text-align: left; padding: 11px 14px;
  border: none; background: transparent; cursor: pointer;
}
/* Per-menu stock strip, shown under the row once the add-on is attached */
.gl-addon-perprod {
  display: flex; align-items: center; justify-content: space-between; gap: 10px;
  padding: 8px 14px 10px; margin-top: -2px;
  border-top: 1px dashed rgba(144,12,39,0.18);
}
.gl-addon-perprod-label { font-size: 11px; color: #6b7280; }
.gl-toggle-sm { width: 38px; height: 21px; }
.gl-toggle-sm .gl-toggle-knob { width: 17px; height: 17px; }
.gl-toggle-sm.on .gl-toggle-knob { transform: translateX(17px); }
.gl-toggle:disabled { opacity: 0.45; cursor: not-allowed; }
.gl-addon-row::before {
  content: ''; position: absolute; left: 0; top: 0; bottom: 0; width: 3px;
  background: #900C27; opacity: 0; transition: opacity 0.15s;
}
.gl-addon-row:hover:not(.selected) { border-color: rgba(144,12,39,0.3); background: #fff; }
.gl-addon-row.selected { border-color: #900C27; background: rgba(144,12,39,0.06); }
.gl-addon-row.selected::before { opacity: 1; }
.gl-addon-row.unavailable { opacity: 0.65; }
.gl-addon-info { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.gl-addon-name { font-size: 13.5px; font-weight: 600; color: #111318; }
.gl-addon-sku { font-size: 11px; font-weight: 600; color: #6b7280; background: #f3f4f6; padding: 2px 7px; border-radius: 5px; font-family: ui-monospace, monospace; }
.gl-addon-oos { font-size: 10.5px; font-weight: 700; color: #ef4444; background: rgba(239,68,68,0.10); padding: 2px 7px; border-radius: 20px; }
.gl-addon-right { display: flex; align-items: center; gap: 12px; flex-shrink: 0; }
.gl-addon-price { font-size: 12.5px; font-weight: 700; color: #900C27; }
.gl-addon-free { font-size: 11.5px; font-weight: 600; color: #c4c8d0; }
.gl-addon-right .gl-toggle { display: inline-block; }

/* ── Branch cards ── */
.gl-branch-cards { display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 8px; }
.gl-branch-card {
  display: flex; align-items: center; gap: 10px;
  padding: 12px 14px; border-radius: 12px;
  border: 1.5px solid #e5e7eb; background: #fafafa;
  cursor: pointer; text-align: left; transition: all 0.15s;
}
.gl-branch-card:hover { border-color: #900C27; background: rgba(144,12,39,0.04); }
.gl-branch-card.active { border-color: #900C27; background: rgba(144,12,39,0.07); }
.gl-bcard-avatar {
  width: 34px; height: 34px; border-radius: 9px; flex-shrink: 0;
  background: linear-gradient(135deg, #900C27, #6f0a1e);
  color: #fff; font-size: 11px; font-weight: 700;
  display: flex; align-items: center; justify-content: center;
}
.gl-branch-card:not(.active) .gl-bcard-avatar { background: #e5e7eb; color: #6b7280; }
.gl-bcard-info { flex: 1; min-width: 0; }
.gl-bcard-name { font-size: 13px; font-weight: 600; color: #111318; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.gl-branch-card.active .gl-bcard-name { color: #900C27; }
.gl-bcard-loc { font-size: 11px; color: #9ca3af; margin-top: 1px; }
.gl-bcard-check {
  width: 18px; height: 18px; border-radius: 50%;
  background: #900C27; display: flex; align-items: center; justify-content: center;
  color: #fff; flex-shrink: 0; opacity: 0; transition: opacity 0.15s;
}
.gl-branch-card.active .gl-bcard-check { opacity: 1; }

/* ── Customisation options ── */
.gl-option-groups { display: flex; flex-direction: column; gap: 10px; margin-bottom: 12px; }
.gl-option-group {
  border: 1.5px solid #f0f1f3; border-radius: 12px; overflow: hidden;
}
.gl-og-header {
  display: flex; flex-direction: column; gap: 6px;
  padding: 12px 14px; background: #fafafa; border-bottom: 1px solid #f3f4f6;
}
.gl-og-labels {
  display: flex; gap: 10px; align-items: center;
  font-size: 11.5px; font-weight: 600; color: #9ca3af; letter-spacing: 0.2px;
  padding-right: 86px; /* reserve space for required+delete */
}
.gl-og-controls {
  display: flex; align-items: center; gap: 8px;
}
.gl-og-required { display: flex; align-items: center; gap: 6px; }
.gl-req-toggle {
  width: 34px; height: 18px; border-radius: 9px; border: none; cursor: pointer; padding: 0;
  background: #e5e7eb; position: relative; transition: background 0.2s; flex-shrink: 0;
}
.gl-req-toggle.on { background: #900C27; }
.gl-req-knob {
  position: absolute; top: 2px; left: 2px;
  width: 14px; height: 14px; border-radius: 50%;
  background: #fff; transition: transform 0.2s;
  box-shadow: 0 1px 3px rgba(0,0,0,0.2); display: block;
}
.gl-req-toggle.on .gl-req-knob { transform: translateX(16px); }
.gl-req-label { font-size: 11.5px; color: #6b7280; font-weight: 500; white-space: nowrap; }
.gl-og-remove {
  width: 28px; height: 28px; border-radius: 7px; border: none;
  background: rgba(239,68,68,0.08); color: #ef4444;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; transition: all 0.15s; flex-shrink: 0;
}
.gl-og-remove:hover { background: rgba(239,68,68,0.16); }

.gl-options-list { padding: 10px 14px 12px; display: flex; flex-direction: column; gap: 8px; }
.gl-option-row { display: flex; align-items: center; gap: 8px; }
.gl-opt-name { flex: 1; }
.gl-opt-avail { display: flex; align-items: center; gap: 5px; flex-shrink: 0; }
.gl-opt-remove {
  width: 26px; height: 26px; border-radius: 6px; border: none;
  background: rgba(239,68,68,0.07); color: #ef4444;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; transition: all 0.15s; flex-shrink: 0;
}
.gl-opt-remove:hover { background: rgba(239,68,68,0.14); }

.gl-add-option {
  display: inline-flex; align-items: center; gap: 5px;
  font-size: 12px; font-weight: 600; color: #900C27;
  border: 1.5px dashed rgba(144,12,39,0.3); border-radius: 8px;
  padding: 6px 12px; background: transparent; cursor: pointer;
  transition: all 0.15s; margin-top: 2px;
}
.gl-add-option:hover { background: rgba(144,12,39,0.05); border-color: #900C27; }

.gl-add-group-btn {
  display: inline-flex; align-items: center; gap: 7px;
  font-size: 13px; font-weight: 600; color: #900C27;
  border: 1.5px dashed rgba(144,12,39,0.3); border-radius: 10px;
  padding: 10px 16px; background: transparent; cursor: pointer;
  transition: all 0.15s; width: 100%; justify-content: center;
}
.gl-add-group-btn:hover { background: rgba(144,12,39,0.05); border-color: #900C27; }

/* ── Pill toggles ── */
.gl-pill-group { display: flex; flex-wrap: wrap; gap: 6px; }
.gl-pill {
  display: inline-flex; align-items: center;
  padding: 5px 13px; border-radius: 20px; font-size: 12.5px; font-weight: 500;
  border: 1.5px solid #e5e7eb; background: #fafafa; color: #6b7280;
  cursor: pointer; transition: all 0.15s; text-transform: capitalize;
}
.gl-pill:hover { border-color: #ef4444; color: #ef4444; background: rgba(239,68,68,0.05); }
.gl-pill.active { border-color: #ef4444; color: #ef4444; background: rgba(239,68,68,0.08); }
.gl-pill-green:hover { border-color: #900C27; color: #900C27; background: rgba(144,12,39,0.06); }
.gl-pill-green.active { border-color: #900C27; color: #900C27; background: rgba(144,12,39,0.08); }

/* ── Actions ── */
.gl-form-actions {
  display: flex; align-items: center; gap: 10px;
  animation: gl-sect-in 0.3s ease both;
}
.gl-btn-cancel {
  padding: 10px 20px; border-radius: 10px;
  border: 1.5px solid #e5e7eb; background: #fff;
  color: #6b7280; font-size: 13.5px; font-weight: 600;
  text-decoration: none; transition: all 0.15s;
}
.gl-btn-cancel:hover { border-color: #d1d5db; background: #f9fafb; color: #374151; }
.gl-btn-submit {
  display: inline-flex; align-items: center; gap: 7px;
  padding: 10px 22px; border-radius: 10px; border: none;
  background: #900C27; color: #fff;
  font-size: 13.5px; font-weight: 600; cursor: pointer;
  transition: background 0.15s, transform 0.1s, box-shadow 0.15s;
  box-shadow: 0 2px 8px rgba(144,12,39,0.25);
}
.gl-btn-submit:hover:not(:disabled) {
  background: #6f0a1e; transform: translateY(-1px);
  box-shadow: 0 4px 14px rgba(144,12,39,0.30);
}
.gl-btn-submit:disabled { opacity: 0.65; cursor: not-allowed; }

/* ── Sidebar ── */
.gl-prod-sidebar { display: flex; flex-direction: column; gap: 12px; position: sticky; top: 80px; }

.gl-sidebar-card {
  background: #fff; border: 1.5px solid #f0f1f3;
  border-radius: 16px; overflow: hidden;
  animation: gl-sect-in 0.3s ease both;
}
.gl-sidebar-card-header {
  display: flex; align-items: center; gap: 7px;
  font-size: 12.5px; font-weight: 700; color: #374151;
  padding: 14px 18px; border-bottom: 1px solid #f0f1f3;
  letter-spacing: 0.1px;
}
.gl-sidebar-card-header svg { color: #9ca3af; }

/* Customer preview card */
.gl-cp-img-wrap { width: 100%; height: 180px; overflow: hidden; background: #f8f9fa; }
.gl-cp-img { width: 100%; height: 100%; object-fit: cover; }
.gl-cp-img-placeholder {
  width: 100%; height: 100%;
  display: flex; align-items: center; justify-content: center;
  background: linear-gradient(135deg, #f0faf8, #e8f5f2);
}
.gl-cp-body { padding: 16px 18px 18px; }
.gl-cp-name { font-size: 16px; font-weight: 800; color: #111318; letter-spacing: -0.3px; }
.gl-cp-desc { font-size: 12px; color: #9ca3af; margin-top: 3px; line-height: 1.4; }
.gl-cp-price { font-size: 20px; font-weight: 800; color: #900C27; margin-top: 10px; letter-spacing: -0.5px; }
.gl-cp-badges { display: flex; flex-wrap: wrap; gap: 5px; margin-top: 10px; }
.gl-cp-badge {
  font-size: 10.5px; font-weight: 600; padding: 3px 8px; border-radius: 20px;
}
.gl-badge-cal { background: #fff3cd; color: #b45309; }
.gl-badge-time { background: #dbeafe; color: #1d4ed8; }
.gl-badge-green { background: #dcfce7; color: #15803d; text-transform: capitalize; }
.gl-badge-red { background: #fee2e2; color: #dc2626; text-transform: capitalize; }

.gl-cp-options { margin-top: 14px; border-top: 1px solid #f3f4f6; padding-top: 12px; }
.gl-cp-og { margin-bottom: 10px; }
.gl-cp-og-name { font-size: 12px; font-weight: 700; color: #111318; display: flex; align-items: center; gap: 5px; margin-bottom: 6px; }
.gl-cp-og-req { font-size: 9.5px; background: rgba(144,12,39,0.10); color: #900C27; padding: 2px 7px; border-radius: 20px; font-weight: 600; }
.gl-cp-og-opt { font-size: 10.5px; color: #9ca3af; font-weight: 400; }
.gl-cp-og-pills { display: flex; flex-wrap: wrap; gap: 5px; }
.gl-cp-opt-pill {
  font-size: 11.5px; font-weight: 600; padding: 4px 10px;
  border: 1.5px solid #e5e7eb; border-radius: 20px;
  background: #fff; color: #374151;
}
.gl-cp-opt-price { color: #9ca3af; margin-left: 3px; font-weight: 500; }

.gl-cp-instructions { margin-top: 14px; border-top: 1px solid #f3f4f6; padding-top: 12px; }
.gl-cp-inst-label { font-size: 12px; font-weight: 700; color: #111318; margin-bottom: 6px; }
.gl-cp-inst-input {
  background: #f3f4f6; border-radius: 8px; padding: 8px 12px;
  font-size: 12px; color: #9ca3af;
}

.gl-cp-cta { display: flex; gap: 8px; margin-top: 14px; }
.gl-cp-btn-secondary, .gl-cp-btn-primary {
  flex: 1; height: 36px; border-radius: 9px;
  font-size: 12.5px; font-weight: 600; cursor: not-allowed; opacity: 0.85;
}
.gl-cp-btn-secondary { background: #fff; border: 1.5px solid #e5e7eb; color: #374151; }
.gl-cp-btn-primary { background: #900C27; border: none; color: #fff; }

/* ── Sticky bar (hidden on desktop) ── */
.gl-sticky-bar {
  display: none;
  position: fixed; bottom: 0; left: 0; right: 0; z-index: 100;
  background: #fff; border-top: 1.5px solid #f0f1f3;
  padding: 12px 16px; gap: 10px;
  box-shadow: 0 -4px 20px rgba(0,0,0,0.06);
}
.gl-sticky-cancel { display: flex; align-items: center; justify-content: center; }
.gl-sticky-submit { flex: 1; justify-content: center; }

/* ── Mobile responsive ── */
@media (max-width: 767px) {
  .gl-form-page { padding-bottom: 88px; }
  .gl-form-header { padding: 14px 0 12px; }
  .gl-form-title { font-size: 18px !important; }

  /* Stack the two-column layout */
  .gl-prod-layout { grid-template-columns: 1fr !important; gap: 12px; }

  /* Hide sidebar preview on mobile — live strip at top is sufficient */
  .gl-prod-sidebar { display: none !important; }

  /* Stack all field rows */
  .gl-field-row { grid-template-columns: 1fr !important; gap: 12px; }
  .gl-field[style*="flex:2"],
  .gl-field[style*="flex:1"] { flex: 1 !important; }

  /* Bigger tap targets */
  .gl-input { height: 46px !important; font-size: 15px !important; }
  .gl-textarea { height: auto !important; min-height: 80px !important; }
  .gl-section { padding: 16px !important; }

  /* Option group header: stack the controls */
  .gl-og-labels { display: none !important; }
  .gl-og-controls { flex-wrap: wrap !important; gap: 8px !important; }
  /* Group name: full width (override inline style="flex:1") */
  .gl-og-controls > input[type="text"].gl-input { width: 100% !important; flex: none !important; }
  /* Type select + max-picks input: side by side */
  .gl-og-controls > select.gl-input { width: calc(50% - 4px) !important; flex: none !important; }
  .gl-og-controls > input[type="number"].gl-input { width: calc(50% - 36px) !important; flex: none !important; }
  .gl-og-required { margin-left: auto !important; }

  /* Option rows: stack name, then price + avail + remove inline */
  .gl-option-row { flex-wrap: wrap !important; gap: 8px !important; }
  .gl-opt-name { width: 100% !important; flex: none !important; }
  /* Price wrap takes remaining space */
  .gl-option-row > .gl-input-prefix-wrap { flex: 1 !important; width: auto !important; }

  /* Branch cards: single column */
  .gl-branch-cards { grid-template-columns: 1fr !important; }

  /* Hide desktop actions bar, show sticky */
  .gl-form-actions { display: none !important; }
  .gl-sticky-bar { display: flex !important; }
}
</style>

<style>
/* ── Dark mode ── */
body.dark-only .gl-form-title { color: rgba(255,255,255,0.92) !important; }
body.dark-only .gl-form-breadcrumb,
body.dark-only .gl-form-breadcrumb a { color: rgba(255,255,255,0.3) !important; }
body.dark-only .gl-form-breadcrumb a:hover { color: #900C27 !important; }

body.dark-only .gl-preview-strip { background: #262932 !important; border-color: rgba(144,12,39,0.25) !important; }
body.dark-only .gl-preview-img { background: linear-gradient(135deg, #2a3340, #243330) !important; }
body.dark-only .gl-preview-name { color: rgba(255,255,255,0.87) !important; }
body.dark-only .gl-preview-tag { background: rgba(255,255,255,0.06) !important; color: rgba(255,255,255,0.45) !important; }
body.dark-only .gl-preview-status.inactive { background: rgba(255,255,255,0.06) !important; color: rgba(255,255,255,0.35) !important; }

body.dark-only .gl-section { background: #262932 !important; border-color: #323644 !important; }
body.dark-only .gl-section-title { color: rgba(255,255,255,0.87) !important; }
body.dark-only .gl-section-desc { color: rgba(255,255,255,0.3) !important; }
body.dark-only .gl-label { color: rgba(255,255,255,0.65) !important; }
body.dark-only .gl-hint { color: rgba(255,255,255,0.2) !important; }
body.dark-only .gl-optional { color: rgba(255,255,255,0.25) !important; }

body.dark-only .gl-input { background: rgba(255,255,255,0.05) !important; border-color: #3d4255 !important; color: rgba(255,255,255,0.87) !important; }
body.dark-only .gl-input:focus { background: rgba(255,255,255,0.07) !important; border-color: #900C27 !important; box-shadow: 0 0 0 3px rgba(144,12,39,0.15) !important; }
body.dark-only .gl-input::placeholder { color: rgba(255,255,255,0.2) !important; }
body.dark-only .gl-input-prefix { color: rgba(255,255,255,0.25) !important; }

body.dark-only .gl-upload-zone { background: rgba(255,255,255,0.03) !important; border-color: #3d4255 !important; }
body.dark-only .gl-upload-zone:hover { background: rgba(144,12,39,0.08) !important; border-color: #900C27 !important; }
body.dark-only .gl-upload-icon { background: #3d4255 !important; }
body.dark-only .gl-upload-label { color: rgba(255,255,255,0.65) !important; }

body.dark-only .gl-status-item { background: rgba(255,255,255,0.03) !important; border-color: #3d4255 !important; }
body.dark-only .gl-status-item:hover { background: rgba(144,12,39,0.10) !important; border-color: #900C27 !important; }
body.dark-only .gl-status-item.on { background: rgba(144,12,39,0.14) !important; border-color: #900C27 !important; }
body.dark-only .gl-status-label { color: rgba(255,255,255,0.87) !important; }
body.dark-only .gl-status-sub { color: rgba(255,255,255,0.3) !important; }
body.dark-only .gl-status-divider { background: #323644 !important; }
body.dark-only .gl-toggle { background: #3d4255 !important; }
body.dark-only .gl-toggle.on { background: #900C27 !important; }

/* Add-on picker — dark */
body.dark-only .gl-addon-count-pill { background: rgba(144,12,39,0.20) !important; color: #4caf97 !important; }
body.dark-only .gl-manage-addons-link { background: rgba(144,12,39,0.15) !important; color: #4caf97 !important; }
body.dark-only .gl-addon-group-label { color: rgba(255,255,255,0.35) !important; }
body.dark-only .gl-addon-group-count { background: rgba(255,255,255,0.06) !important; color: rgba(255,255,255,0.5) !important; }
body.dark-only .gl-addon-group-action { color: #4caf97 !important; }
body.dark-only .gl-addon-row { background: rgba(255,255,255,0.03) !important; border-color: #3d4255 !important; }
body.dark-only .gl-addon-row:hover { background: rgba(144,12,39,0.10) !important; border-color: #900C27 !important; }
body.dark-only .gl-addon-row.selected { background: rgba(144,12,39,0.14) !important; border-color: #900C27 !important; }
body.dark-only .gl-addon-hit { background: transparent !important; }
body.dark-only .gl-addon-perprod { border-top-color: rgba(255,255,255,0.12) !important; }
body.dark-only .gl-addon-perprod-label { color: rgba(255,255,255,0.45) !important; }
body.dark-only .gl-addon-name { color: rgba(255,255,255,0.85) !important; }
body.dark-only .gl-addon-sku { background: rgba(255,255,255,0.06) !important; color: rgba(255,255,255,0.5) !important; }
body.dark-only .gl-addon-price { color: #4caf97 !important; }
body.dark-only .gl-addon-free { color: rgba(255,255,255,0.25) !important; }
body.dark-only .gl-addons-empty { border-color: #3d4255 !important; }
body.dark-only .gl-addons-empty p { color: rgba(255,255,255,0.3) !important; }

body.dark-only .gl-branch-card { background: rgba(255,255,255,0.04) !important; border-color: #3d4255 !important; }
body.dark-only .gl-branch-card:hover { background: rgba(144,12,39,0.10) !important; border-color: #900C27 !important; }
body.dark-only .gl-branch-card.active { background: rgba(144,12,39,0.12) !important; border-color: #900C27 !important; }
body.dark-only .gl-branch-card:not(.active) .gl-bcard-avatar { background: #3d4255 !important; color: rgba(255,255,255,0.4) !important; }
body.dark-only .gl-bcard-name { color: rgba(255,255,255,0.75) !important; }
body.dark-only .gl-branch-card.active .gl-bcard-name { color: #4caf97 !important; }
body.dark-only .gl-bcard-loc { color: rgba(255,255,255,0.3) !important; }

body.dark-only .gl-option-group { border-color: #323644 !important; }
body.dark-only .gl-og-header { background: rgba(255,255,255,0.02) !important; border-bottom-color: #323644 !important; }
body.dark-only .gl-req-toggle { background: #3d4255 !important; }
body.dark-only .gl-req-toggle.on { background: #900C27 !important; }
body.dark-only .gl-req-label { color: rgba(255,255,255,0.35) !important; }
body.dark-only .gl-og-remove { background: rgba(239,68,68,0.12) !important; }
body.dark-only .gl-opt-remove { background: rgba(239,68,68,0.10) !important; }
body.dark-only .gl-add-option { border-color: rgba(144,12,39,0.25) !important; color: #4caf97 !important; }
body.dark-only .gl-add-option:hover { background: rgba(144,12,39,0.10) !important; }
body.dark-only .gl-add-group-btn { border-color: rgba(144,12,39,0.25) !important; color: #4caf97 !important; }
body.dark-only .gl-add-group-btn:hover { background: rgba(144,12,39,0.10) !important; }

body.dark-only .gl-pill { background: rgba(255,255,255,0.04) !important; border-color: #3d4255 !important; color: rgba(255,255,255,0.45) !important; }
body.dark-only .gl-pill:hover { background: rgba(239,68,68,0.10) !important; border-color: rgba(239,68,68,0.4) !important; }
body.dark-only .gl-pill.active { background: rgba(239,68,68,0.12) !important; border-color: rgba(239,68,68,0.5) !important; color: #f87171 !important; }
body.dark-only .gl-pill-green:hover { background: rgba(144,12,39,0.10) !important; border-color: rgba(144,12,39,0.4) !important; color: #4caf97 !important; }
body.dark-only .gl-pill-green.active { background: rgba(144,12,39,0.12) !important; border-color: rgba(144,12,39,0.5) !important; color: #4caf97 !important; }

body.dark-only .gl-btn-cancel { background: #323644 !important; border-color: #3d4255 !important; color: rgba(255,255,255,0.55) !important; }
body.dark-only .gl-btn-cancel:hover { background: #3d4255 !important; color: rgba(255,255,255,0.75) !important; }

body.dark-only .gl-sidebar-card { background: #262932 !important; border-color: #323644 !important; }
body.dark-only .gl-sidebar-card-header { color: rgba(255,255,255,0.65) !important; border-bottom-color: #323644 !important; }
body.dark-only .gl-cp-img-placeholder { background: linear-gradient(135deg, #2a3340, #243330) !important; }
body.dark-only .gl-cp-name { color: rgba(255,255,255,0.87) !important; }
body.dark-only .gl-cp-desc { color: rgba(255,255,255,0.3) !important; }
body.dark-only .gl-cp-price { color: #4caf97 !important; }
body.dark-only .gl-cp-og-name { color: rgba(255,255,255,0.75) !important; }
body.dark-only .gl-cp-opt-pill { background: rgba(255,255,255,0.04) !important; border-color: #3d4255 !important; color: rgba(255,255,255,0.65) !important; }
body.dark-only .gl-cp-inst-input { background: rgba(255,255,255,0.05) !important; color: rgba(255,255,255,0.3) !important; }
body.dark-only .gl-cp-inst-label { color: rgba(255,255,255,0.75) !important; }
body.dark-only .gl-cp-instructions { border-top-color: #323644 !important; }
body.dark-only .gl-cp-options { border-top-color: #323644 !important; }
body.dark-only .gl-cp-btn-secondary { background: rgba(255,255,255,0.04) !important; border-color: #3d4255 !important; color: rgba(255,255,255,0.55) !important; }

body.dark-only .gl-sticky-bar { background: #1d1e26 !important; border-top-color: #323644 !important; }
</style>
